import localeService from 'commons/localeService/localeService';
import { PUBLISHING_SCOPE } from 'commons/publishing/publishingScope';

/**
 * The sample shops are BU-configured Spreadshops that price at cost (see DEV-229487). Which one a
 * partner orders through follows **the scope and the locale, and nothing else** — not the entry
 * point, not the composition, and not the shop the composition is published in.
 *
 * Each scope has its own set, for reporting and pricing (DEV-232368). A Marketplace sample still
 * goes through a Spreadshop, though, just one reserved for Marketplace: the marketplace checkout
 * cannot price a sample at all, because it reads none of the pricing configuration BU can set.
 *
 * Note this is a different set from `localeService`'s `shopIdForLocale`, which holds the public D2C
 * shops.
 */
const SAMPLE_ORDER_SHOP_IDS: Record<string, Record<string, number>> = {
  // Per DEV-231126.
  [PUBLISHING_SCOPE.SHOP.key]: {
    // EU
    de_DE: 1569988, // PartnerSampleOrderDE
    de_AT: 1569990, // PartnerSampleOrderAT
    en_GB: 1569993, // PartnerSampleOrderUK
    en_IE: 1569994, // PartnerSampleOrderIE
    fr_FR: 1569995, // PartnerSampleOrderFR
    de_CH: 1569996, // PartnerSampleOrderCHde
    fr_CH: 1569997, // PartnerSampleOrderCHfr
    it_CH: 1569998, // PartnerSampleOrderCHit
    nl_NL: 1570000, // PartnerSampleOrderNL
    nl_BE: 1570002, // PartnerSampleOrderBEnl
    fr_BE: 1570003, // PartnerSampleOrderBEfr
    no_NO: 1570004, // PartnerSampleOrderNO
    fi_FI: 1570005, // PartnerSampleOrderFI
    sv_SE: 1570006, // PartnerSampleOrderSE
    da_DK: 1570007, // PartnerSampleOrderDK
    es_ES: 1570008, // PartnerSampleOrderES
    it_IT: 1570009, // PartnerSampleOrderIT
    pl_PL: 1570010, // PartnerSampleOrderPL

    // NA
    en_US: 2035841, // PartnerSampleOrderUS
    en_CA: 2035842, // PartnerSampleOrderCAen
    fr_CA: 2035843, // PartnerSampleOrderCAfr
    en_AU: 2035844, // PartnerSampleOrderAU
  },

  // Per DEV-232368.
  [PUBLISHING_SCOPE.MP.key]: {
    // EU
    de_DE: 1590929, // PartnerSampleOrderMPDE
    de_AT: 1590931, // PartnerSampleOrderMPAT
    en_GB: 1590933, // PartnerSampleOrderMPUK
    en_IE: 1590934, // PartnerSampleOrderMPIE
    fr_FR: 1590935, // PartnerSampleOrderMPFR
    de_CH: 1590936, // PartnerSampleOrderMPCHde
    fr_CH: 1590937, // PartnerSampleOrderMPCHfr
    it_CH: 1590938, // PartnerSampleOrderMPCHit
    nl_NL: 1590939, // PartnerSampleOrderMPNL
    nl_BE: 1590940, // PartnerSampleOrderMPBEnl
    fr_BE: 1590941, // PartnerSampleOrderMPBEfr
    no_NO: 1590942, // PartnerSampleOrderMPNO
    fi_FI: 1590943, // PartnerSampleOrderMPFI
    sv_SE: 1590944, // PartnerSampleOrderMPSE
    da_DK: 1590945, // PartnerSampleOrderMPDK
    es_ES: 1590946, // PartnerSampleOrderMPES
    it_IT: 1590947, // PartnerSampleOrderMPIT
    pl_PL: 1590948, // PartnerSampleOrderMPPL

    // NA
    en_US: 2050562, // PartnerSampleOrderMPUS
    en_CA: 2050563, // PartnerSampleOrderMPCAen
    fr_CA: 2050564, // PartnerSampleOrderMPCAfr
    en_AU: 2050565, // PartnerSampleOrderMPAU
  },
};

/**
 * Per-scope, per-platform fallback for a locale without its own sample shop. EU has a shop built for
 * exactly this (`PartnerSampleOrderEU` / `…MPEU`); NA has none, so the US shop stands in.
 */
const FALLBACK_SAMPLE_ORDER_SHOP_IDS: Record<string, Record<string, number>> = {
  [PUBLISHING_SCOPE.SHOP.key]: {
    EU: 1569991, // PartnerSampleOrderEU
    NA: 2035841, // PartnerSampleOrderUS
  },
  [PUBLISHING_SCOPE.MP.key]: {
    EU: 1590932, // PartnerSampleOrderMPEU
    NA: 2050562, // PartnerSampleOrderMPUS
  },
};

/**
 * The sample shop for the given scope and locale, defaulting to the locale the partner area is
 * running in. Returns `undefined` when the scope or, for a locale without its own shop, the platform
 * is unknown, which the journey reports as "cannot price" rather than guessing a shop.
 */
export const getSampleOrderShopId = (
  scopeKey: string | undefined,
  locale: string | undefined = localeService.getLocale(),
): number | undefined => {
  if (!scopeKey) {
    return undefined;
  }

  const localeShopId = locale
    ? SAMPLE_ORDER_SHOP_IDS[scopeKey]?.[locale]
    : undefined;

  return (
    localeShopId ?? FALLBACK_SAMPLE_ORDER_SHOP_IDS[scopeKey]?.[SPRD.PLATFORM]
  );
};

const ALL_SAMPLE_ORDER_SHOP_IDS = new Set<number>(
  [
    ...Object.values(SAMPLE_ORDER_SHOP_IDS),
    ...Object.values(FALLBACK_SAMPLE_ORDER_SHOP_IDS),
  ].flatMap((shopIds) => Object.values(shopIds)),
);

/**
 * Whether the given shop is one of the sample shops, of either scope. The list is complete: it holds
 * every shop a sample order can be routed through, including the per-platform fallback shops that no
 * locale maps to. statistics-service delivers the shop id as a number; a string is accepted as well.
 */
export const isSampleOrderShop = (
  shopId: number | string | undefined,
): boolean =>
  shopId !== undefined &&
  shopId !== '' &&
  ALL_SAMPLE_ORDER_SHOP_IDS.has(Number(shopId));

export const hasDedicatedSampleOrderShop = (
  scopeKey: string,
  locale: string,
): boolean => Boolean(SAMPLE_ORDER_SHOP_IDS[scopeKey]?.[locale]);
